/*
  Расход коммуникаций — §3.11.5.

  Экран отвечает на два разных вопроса, и их нельзя смешивать: «во что обойдётся
  волна, которую я собираюсь запустить» и «сколько уже потрачено». Первый — до
  действия, и он важнее: цена узнаётся прежде, чем нажата кнопка, а не из счёта
  следующего месяца.

  Незаполненный тариф показывается как незаполненный. Заказчик, увидевший
  заниженную цифру, запустит рассылку и получит счёт вдвое больше — а виноват
  будет экран, который промолчал.
*/

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client.js'
import { date, lastMonths, money, monthKey } from '../api/format.js'
import { Empty, Field, Loading, Notice, Select } from '../components/Common.jsx'

const MONTHS_TO_CHOOSE = 12

/** Границы месяца «ГГГГ-ММ» для запроса: начало и первое число следующего. */
function monthRange(key) {
  const [year, month] = key.split('-').map(Number)
  const first = new Date(year, month - 1, 1)
  const next = new Date(year, month, 1)
  return { since: monthKey(first) + '-01', until: monthKey(next) + '-01' }
}

export default function UsagePage() {
  const [report, setReport] = useState(null)
  const [estimate, setEstimate] = useState(null)
  const [error, setError] = useState(null)
  const [month, setMonth] = useState(() => monthKey(new Date()))
  const [plan, setPlan] = useState({ accounts: 900, messages: 1, minutes: '0', postal: 0 })

  const months = useMemo(() => lastMonths(MONTHS_TO_CHOOSE), [])

  useEffect(() => {
    const { since, until } = monthRange(month)
    // Ответ на прежний месяц, пришедший после смены, выбрасывается (аудит 26.10.2026,
    // причина 5): под названием нового месяца оставались цифры предыдущего.
    let current = true
    setReport(null)
    api
      .usage(since, until)
      .then((answer) => {
        if (current) setReport(answer)
      })
      .catch((failure) => {
        if (current) setError(failure)
      })
    return () => {
      current = false
    }
  }, [month])

  const calculate = useCallback(async () => {
    setError(null)
    try {
      setEstimate(
        await api.estimateWave({
          accounts: Number(plan.accounts) || 0,
          messages_per_account: Number(plan.messages) || 0,
          voice_minutes_per_account: String(plan.minutes || '0'),
          postal_per_account: Number(plan.postal) || 0,
        }),
      )
    } catch (failure) {
      setError(failure)
    }
  }, [plan])

  // Все четыре величины - числа не длиннее четырёх знаков, поэтому ширина
  // наименьшая: поле в двести пикселей под «900» обещает абзац.
  const field = (name, label, extra = {}) => (
    <label className="field field--xs">
      <span className="field__label">{label}</span>
      <input
        className="field__input"
        value={plan[name]}
        onChange={(e) => setPlan({ ...plan, [name]: e.target.value })}
        inputMode="decimal"
        {...extra}
      />
    </label>
  )

  return (
    <>
      <header className="head">
        <div>
          <h1 className="head__title">Расход коммуникаций</h1>
          <div className="head__note">
            Трафик передаётся вам по себестоимости. Цена волны считается до её запуска.
          </div>
        </div>
      </header>

      <Notice error={error} />

      <section className="section card">
        <h2 className="section__title">Во что обойдётся волна</h2>
        <div className="row">
          {field('accounts', 'Счетов в волне')}
          {field('messages', 'Сообщений на счёт')}
          {field('minutes', 'Минут разговора на счёт')}
          {field('postal', 'Писем на счёт')}
          <button type="button" className="button" onClick={calculate}>
            Посчитать
          </button>
        </div>

        {estimate ? (
          <div className={`notice notice--${estimate.is_complete ? 'ok' : 'warn'}`}>
            <div className="notice__what">
              {estimate.is_complete ? money(estimate.total) : 'Оценить целиком нечем'}
            </div>
            <div className="notice__do">{estimate.explanation}</div>
            {!estimate.is_complete ? (
              <p className="notice__do">
                <Link to="/settings">Заполнить тарифы в настройках организации</Link>
              </p>
            ) : null}
            {estimate.lines.length > 0 ? (
              <ul className="reasons">
                {estimate.lines.map((line) => (
                  <li key={line.kind}>
                    {line.title}: {line.quantity} × {money(line.unit_price)} ={' '}
                    {money(line.amount)}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="section">
        <h2 className="section__title">Уже потрачено</h2>
        <div className="row">
          <Field label="Месяц" width="s">
            <Select value={month} onChange={setMonth} options={months} empty={null} />
          </Field>
        </div>
        {!report ? <Loading what="Считаем расход" /> : null}

        {report && report.lines.length === 0 ? (
          <Empty title="Расхода за этот месяц не было">
            Здесь появятся отправленные сообщения, минуты разговоров и почтовые отправления с ценой
            на момент действия. За выбранный месяц действий не было - выберите другой месяц.
          </Empty>
        ) : null}

        {report && report.lines.length > 0 ? (
          <>
            <div className="card table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>Статья</th>
                    <th className="table__num">Количество</th>
                    <th className="table__num">Сумма</th>
                    <th>Без цены</th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((line) => (
                    <tr key={line.kind}>
                      <td>{line.title}</td>
                      <td className="table__num" data-label="Количество">
                        {line.quantity}
                      </td>
                      <td className="table__num" data-label="Сумма">
                        {money(line.amount)}
                      </td>
                      <td data-label="Без цены">
                        {line.unpriced > 0 ? (
                          <span className="tag tag--warn">
                            {line.unpriced} - тариф не был задан
                          </span>
                        ) : (
                          <span className="muted">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row row--spread">
              <span className="muted">
                Период: {date(report.since)} - {date(report.until)}
              </span>
              <strong>Итого: {money(report.total)}</strong>
            </div>
            {report.has_unpriced ? (
              <div className="notice notice--warn">
                <div className="notice__what">
                  Часть расхода записана без цены: тариф не был задан в момент действия.
                </div>
                <div className="notice__do">
                  Это не «бесплатно», а «неизвестно, сколько стоило». Заполните тарифы, чтобы
                  следующие действия считались.
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </>
  )
}
